import { DataGrid, DataGridColumn, DataGridRef } from '@cimplico/components';
import { Ref, useState } from 'react';
import { ROW_SIZES } from '../../constants';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { WorksheetWithAccount } from '../../types/worksheet';
import { ScaleIcon } from '@heroicons/react/24/outline';
import TrialBalanceRow from './TrialBalanceRow';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import useResizeObserver from 'use-resize-observer';

// When the size of the table reaches this, switch to a smaller layout
const TABLE_RESIZE_BREAKPOINT = 880;

const largeScreenColumns: DataGridColumn[] = [
    {
        header: (
            <p data-intercom-target="trial-balance-table-header-account" className="font-medium text-xs text-gray-600 dark:text-gray-400">
                ACCOUNT
            </p>
        ),
        alignment: 'left',
        width: 'minmax(200px,2fr)',
    },
    {
        header: 'Current Amount',
        alignment: 'right',
        width: '1fr',
    },
    {
        header: 'Amount (LY)',
        alignment: 'right',
        width: '1fr',
    },
    {
        header: (
            <p data-intercom-target="trial-balance-table-header-variance" className="font-medium text-xs text-gray-600 dark:text-gray-400">
                VARIANCE
            </p>
        ),
        alignment: 'right',
        width: '1fr',
    },
    {
        header: (
            <p data-intercom-target="trial-balance-table-header-files" className="font-medium text-xs text-gray-600 dark:text-gray-400">
                FILES
            </p>
        ),
        alignment: 'center',
        width: '0.5fr',
    },
    {
        header: '',
        alignment: 'center',
        width: '0.25fr',
    },
    {
        header: (
            <p
                data-intercom-target="trial-balance-table-header-outstanding"
                className="font-medium text-xs text-gray-600 dark:text-gray-400"
            >
                OUTSTANDING
            </p>
        ),
        alignment: 'center',
        width: '0.75fr',
    },
];

const smallScreenColumns: DataGridColumn[] = [
    {
        header: 'Account',
        alignment: 'left',
        width: 'minmax(200px,2fr)',
    },
    {
        header: 'Current Amount',
        alignment: 'right',
        width: '1fr',
    },
    {
        header: 'Amount (LY)',
        alignment: 'right',
        width: '1fr',
    },
    {
        header: '',
        alignment: 'center',
        width: '0.5fr',
    },
];

type TrialBalanceTableProps = {
    /** An array of WorksheetWithAccount objects, which represent the worksheets to be displayed in the table */
    worksheets?: WorksheetWithAccount[];
    /** The total number of worksheets that match the current filter criteria */
    totalItems?: number;
    /** A boolean value that indicates whether the table should show a loading state */
    isLoading: boolean;
    /** A callback when the user changes the page. */
    onPageChange: (page: number) => void;
    /**  A callback when the user changes the number of rows per page. */
    onLimitChange: (limit: number) => void;
    /** A React ref object that points to the DataGrid component. */
    tableRef: Ref<DataGridRef>;
    /** A callback when the user selects or deselects worksheets */
    onSelect: (worksheets: WorksheetWithAccount[]) => void;
    /** A callback when the user wants to update the trial balance  */
    onUpdate: () => void;
};

function TrialBalanceTable({
    worksheets = [],
    totalItems = 0,
    isLoading,
    onLimitChange,
    onPageChange,
    tableRef,
    onSelect,
    onUpdate,
}: TrialBalanceTableProps): JSX.Element {
    const [showSmallColumns, setShowSmallColumns] = useState(false);
    const { readOnly } = useWorkpaperContext();
    const [currentFileDrawer, setCurrentFileDrawer] = useState<string>();

    /**
     * Attach a ResizeObserver to the table to determine when it should render in "smallScreen" mode.
     */
    const { ref } = useResizeObserver<HTMLDivElement>({
        onResize: ({ width = 1 }) => {
            // setTimeout trick to stop the ResizeObserver loop issue https://github.com/juggle/resize-observer/issues/103
            setTimeout(() => {
                if (width <= TABLE_RESIZE_BREAKPOINT) {
                    setShowSmallColumns(true);
                } else {
                    setShowSmallColumns(false);
                }
            }, 0);
        },
    });

    /**
     * Select all worksheets that have a matching row id.
     * @param ids Selected row ids
     */
    function selectWorksheets(ids: string[]): void {
        const selectedWorksheets = worksheets.filter((ws) => ids.includes(ws.id));
        onSelect(selectedWorksheets);
    }

    return (
        <div ref={ref} className="min-w-[650px] relative">
            <DataGrid
                ref={tableRef}
                columns={showSmallColumns ? smallScreenColumns : largeScreenColumns}
                rowSizes={ROW_SIZES}
                defaultRowSizeIndex={1}
                totalRows={totalItems}
                stickyHeader
                isLoading={isLoading}
                onSelect={selectWorksheets}
                emptyMessage={
                    !isLoading && (
                        <EmptyStateNew
                            icon={<ScaleIcon />}
                            actionText="Update"
                            helpLink="https://intercom.help/cimplico/en/articles/8696513-trial-balance-basics"
                            helpText="Update the trial balance or change filters to see available accounts."
                            title="No accounts found"
                            disableActionButton={readOnly}
                            onClick={onUpdate}
                            showButtonIcon={false}
                        />
                    )
                }
                onPageChange={onPageChange}
                onRowSizeChange={onLimitChange}
            >
                {worksheets.map((worksheet, count) => (
                    <div key={worksheet.id} {...(count < 1 ? { 'data-intercom-target': 'trial-balance-top-account-in-table' } : {})}>
                        <TrialBalanceRow
                            worksheet={worksheet}
                            // key={worksheet.id}
                            smallScreen={showSmallColumns}
                            currentFileDrawer={currentFileDrawer}
                            onFileDrawerOpen={setCurrentFileDrawer}
                        />
                    </div>
                ))}
            </DataGrid>
        </div>
    );
}

export default TrialBalanceTable;
